<template>
    <skeleton v-if="isLoading" />
    <form
        v-else
        @submit="onSubmit"
    >
        <content-reset :body="formMarkup" />

        <div
            v-if="activeFacilityCount > 1"
            class="mt-6 flex flex-col"
        >
            <label
                for="select_facility"
                class="font-semibold"
            >
                {{ facilityLabel }}
            </label>
            <span class="w-full">
                <facility-select
                    id="select_facility"
                    v-model="facilityId"
                    :facility-groups="facilityGroups"
                    :invalid="errors.facilityId !== undefined"
                />
            </span>
            <span
                id="facility-error"
                class="text-small text-red-600"
            >
                {{ errors.facilityId || '&nbsp;' }}
            </span>
        </div>

        <div class="mt-2 grid gap-x-4 sm:grid-cols-2">
            <div>
                <label class="font-semibold">First Name</label>
                <span class="w-full">
                    <input-text
                        id="first-name"
                        v-model="firstNameValue"
                        v-bind="firstNameProps"
                        class="w-full"
                        placeholder="First Name"
                        maxlength="255"
                        aria-describedby="first-name-error"
                        autofocus
                    />
                </span>
                <span
                    id="first-name-error"
                    class="text-small text-red-600"
                >
                    {{ errors.firstName || '&nbsp;' }}
                </span>
            </div>

            <div>
                <label class="font-semibold">Last Name</label>
                <span class="w-full">
                    <input-text
                        id="last-name"
                        v-model="lastNameValue"
                        v-bind="lastNameProps"
                        class="w-full"
                        placeholder="Last Name"
                        maxlength="255"
                        aria-describedby="last-name-error"
                    />
                </span>
                <span
                    id="last-name-error"
                    class="text-small text-red-600"
                >
                    {{ errors.lastName || '&nbsp;' }}
                </span>
            </div>
        </div>

        <div>
            <label class="font-semibold">Email Address</label>
            <span class="p-input-icon-left w-full">
                <i class="pi pi-envelope"></i>
                <input-text
                    id="email"
                    v-model="emailValue"
                    v-bind="emailProps"
                    inputmode="email"
                    type="email"
                    class="w-full !text-[16px]"
                    :class="{ 'p-invalid': errors.email }"
                    placeholder="Email Address"
                    aria-describedby="email-error"
                />
            </span>
            <span
                id="email-error"
                class="text-small text-red-600"
                >{{ errors.email || '&nbsp;' }}</span
            >
        </div>

        <div class="mt-1">
            <label class="font-semibold">Subject</label>
            <span class="w-full">
                <input-text
                    id="subject"
                    v-model="subjectValue"
                    v-bind="subjectProps"
                    class="w-full"
                    placeholder="Subject"
                    maxlength="255"
                    aria-describedby="subject-error"
                />
            </span>
            <span
                id="subject-error"
                class="text-small text-red-600"
            >
                {{ errors.subject || '&nbsp;' }}
            </span>
        </div>

        <div class="mt-1">
            <label class="font-semibold">Message</label>
            <span class="w-full">
                <prime-textarea
                    id="message"
                    v-model="messageValue"
                    v-bind="messageProps"
                    class="w-full"
                    placeholder="Message"
                    :rows="5"
                    :cols="30"
                    maxlength="1000"
                    aria-describedby="message-error"
                />
            </span>
            <span
                id="message-error"
                class="text-small text-red-600"
            >
                {{ errors.message || '&nbsp;' }}
            </span>
        </div>

        <div class="mt-2">
            <rp-recaptcha @token="handleRecaptchaToken" />
            <span class="text-small text-red-600">
                {{ errors.recaptchaToken || '&nbsp;' }}
            </span>
        </div>

        <div class="mt-2 flex justify-end">
            <prime-button
                type="submit"
                label="Submit"
                :loading="isPending"
            />
        </div>
    </form>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button'
import InputText from 'primevue/inputtext'
import PrimeTextarea from 'primevue/textarea'
import RpRecaptcha from '~/components/Verification/RpRecaptcha.vue'
import Skeleton from 'primevue/skeleton'
import ContentReset from '~/components/ContentReset.vue'
import { object, string } from 'yup'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import { computed, onMounted, toRef, watch } from 'vue'
import { toTypedSchema } from '@vee-validate/yup'
import { useForm } from 'vee-validate'
import { useToast } from 'primevue/usetoast'
import { useCreateAnonymousTicketMutation } from '~/components/Tickets/use-create-anonymous-ticket-mutation'
import { TicketConfirmationTicketFragment } from '~/components/Tickets/TicketConfirmation.vue'
import FacilitySelect from '~/components/Verification/FacilitySelect.vue'
import { useI18n } from 'vue-i18n'
import { graphql } from '~/resources/graphql'

const props = defineProps<{
    formMarkup: string
    // If this props is not provided, a facility selector will be shown
    facilityIdContext?: string
}>()

const emit = defineEmits<{
    (e: 'ticketCreated', payload: typeof TicketConfirmationTicketFragment): void
}>()

const { t } = useI18n({ useScope: 'global' })
const facilityIdContextRef = toRef(props, 'facilityIdContext')

const query = useRpQuery({ orgLevel: true })
// TODO: when there are more than 100 facilities
const { data, isLoading } = useQuery({
    queryKey: ['AnonymousContactUsTicketFormQuery', facilityIdContextRef],
    placeholderData: previousData => previousData,
    queryFn: () => {
        // If a facility has been provided, no need for this query
        if (facilityIdContextRef.value !== undefined) {
            return null
        }

        return query(
            graphql(/* GraphQL */ `
                query AnonymousContactUsTicketFormQuery {
                    organization {
                        facilityLocationGroups {
                            id
                            name
                            facilities {
                                id
                                label
                            }
                        }
                    }
                }
            `)
        )
    }
})

const facilityGroups = computed(() => {
    // If a facility has been provided, no need for this
    if (props.facilityIdContext !== undefined) {
        return []
    }

    return [
        ...(data.value?.organization.facilityLocationGroups ?? []).map(
            group => ({
                id: group.id,
                name: group.name,
                facilities: group.facilities
            })
        )
    ]
})

const activeFacilities = computed(() =>
    facilityGroups.value.flatMap(group => group.facilities)
)

const activeFacilityCount = computed(() => activeFacilities.value?.length ?? 0)
const facilityLabel = computed(() => t('facility', 1))

const schema = toTypedSchema(
    object({
        facilityId: string().required('Please select a facility'),
        email: string()
            .email('Please enter a valid email')
            .required('Please enter an email'),
        firstName: string()
            .max(255)
            .min(1, '1 or more characters required')
            .required('1 or more characters required'),
        lastName: string()
            .max(255)
            .min(2, '2 or more characters required')
            .required('2 or more characters required'),
        subject: string().max(255).required('Please enter a subject'),
        message: string().max(1000).required('Please enter a message'),
        recaptchaToken: string().required('Please complete the captcha')
    })
)

const { errors, defineField, defineComponentBinds, handleSubmit } = useForm({
    validationSchema: schema
})

const [emailValue, emailProps] = defineField('email', {
    validateOnModelUpdate: false,
    validateOnBlur: true
})

const [firstNameValue, firstNameProps] = defineField('firstName', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const [lastNameValue, lastNameProps] = defineField('lastName', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const [subjectValue, subjectProps] = defineField('subject', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const [messageValue, messageProps] = defineField('message', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const recaptchaValue = defineComponentBinds('recaptchaToken', {
    validateOnBlur: false
})

const [facilityId] = defineField('facilityId')
// This is for handling the 1 facility case
watch(activeFacilities, newValue => {
    if (newValue === null) {
        return
    }

    if (newValue.length === 1) {
        facilityId.value = newValue[0].id
    }
})

// This is for handling the case where a facility has been provided
onMounted(() => {
    if (props.facilityIdContext !== undefined) {
        facilityId.value = props.facilityIdContext
    }
})

const toast = useToast()
const { mutate, isPending } = useCreateAnonymousTicketMutation()
const onSubmit = handleSubmit(values => {
    mutate(
        {
            ...values
        },
        {
            onSuccess: data => {
                emit('ticketCreated', data.record)
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Ticket Created',
                    life: 3000,
                    closable: false
                })
            }
        }
    )
})

function handleRecaptchaToken(token: string) {
    recaptchaValue.value['onUpdate:modelValue'](token)
    recaptchaValue.value.onBlur()
}
</script>
